<app-back-home />

<div>
    <header class="bg-transparent h-1/6 mb-4 flex justify-between items-center pb-10 pt-5">
        <div class="flex flex-row w-1/4 justify-start">
            @if (season(); as season) {
                <div class="pr-4">
                    <h2 class="text-xl font-bold text-[var(--color-text-strong)]">{{ season.title }}</h2>
                    <p class="text-[rgb(var(--ui-5))]">
                        {{ season.startDate | date: 'dd.MM.yyyy' : undefined : 'en' }} -
                        {{ season.endDate | date: 'dd.MM.yyyy' : undefined : 'en' }}
                    </p>
                </div>
                <p
                    class="h-fit text-black text-xs font-semibold px-3 mt-1 rounded-full flex justify-center items-center align-middle"
                    [class]="seasonStateBackgroundClass[season.status]"
                >
                    {{ season.status }}
                </p>
            } @else {
                <div class="animate-pulse">
                    <div class="bg-[var(--base-2)] h-8 w-40 rounded-md"></div>
                    <div class="bg-[var(--base-2)] h-4 w-20 rounded-md mt-2"></div>
                </div>
            }
        </div>

        <div class="flex gap-3">
            @if (season(); as season) {
                @if (isExportApplicationsButtonEnabled) {
                    <app-season-details-export-application-button [seasonId]="season.id" />
                }

                @if (seasonHasPayoutApplicationsSignal()) {
                    <div class="relative inline-flex">
                        <sh-button
                            color="secondary"
                            icon="document"
                            iconPosition="left"
                            layout="compact"
                            (click)="downloadPayoutFiles()"
                            ariaLabel="Download button"
                            [label]="'seasons.season-details.buttons.payout-files' | translate"
                        />
                        @if (isDownloading) {
                            <div
                                class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md bg-[var(--base-0)]"
                            >
                                <sh-spinner size="s" />
                            </div>
                        }
                    </div>
                }
                @if (season.status === seasonStatus.ACTIVE) {
                    <sh-button
                        color="primary"
                        icon="arrow-line-right-s"
                        iconPosition="left"
                        layout="compact"
                        (click)="releasePayout()"
                        [disabled]="!isBudgetAvailableSignal() || !isApplicationsSelectedForPayoutSignal()"
                        ariaLabel="Release payout"
                        [label]="'seasons.season-details.buttons.release-payout' | translate"
                    />
                }
                <sh-button
                    color="primary"
                    icon="arrow-line-right-s"
                    iconPosition="left"
                    layout="compact"
                    (click)="closeSeason()"
                    [disabled]="!hasSeasonApplicationPeriodEndedSignal() || isSeasonClosedSignal()"
                    ariaLabel="close season"
                    [label]="'seasons.season-details.buttons.close-season' | translate"
                />
            }
        </div>
    </header>

    <sh-tabs>
        <sh-tab-item
            [active]="activeTab === 'applications'"
            [label]="'seasons.season-details.tabs.application' | translate"
            (clicked)="activeTab = 'applications'"
        />
        <sh-tab-item
            [active]="activeTab === 'settings'"
            [label]="'seasons.season-details.tabs.settings-and-weighting' | translate"
            size="l"
            (clicked)="activeTab = 'settings'"
        />
    </sh-tabs>

    @if (activeTab === 'applications') {
        <div>
            <div
                class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[3fr_3fr_4fr_4fr] gap-2 my-8"
                [class.animate-pulse]="isLoading()"
            >
                @if (financialOverviewSignal()) {
                    <app-financial-card
                        [title]="'seasons.season-details.application-cards.total-applications.title' | translate"
                        [subtitle]="'seasons.season-details.application-cards.total-applications.subtitle' | translate"
                        [amount]="financialOverviewSignal()?.totalApplications"
                    />
                    <app-financial-card
                        [title]="'seasons.season-details.application-cards.selected-applications.title' | translate"
                        [subtitle]="
                            'seasons.season-details.application-cards.selected-applications.subtitle' | translate
                        "
                        [amount]="financialOverviewSignal()?.selectedApplications"
                        [ammountShouldBeGreen]="true"
                    />
                    <app-financial-card
                        [title]="'seasons.season-details.application-cards.total-budget.title' | translate"
                        [subtitle]="'seasons.season-details.application-cards.total-budget.subtitle' | translate"
                        [amount]="financialOverviewSignal()?.totalBudget"
                        [currency]="true"
                    />
                    <app-financial-card
                        [title]="'seasons.season-details.application-cards.available-budget.title' | translate"
                        [subtitle]="'seasons.season-details.application-cards.available-budget.subtitle' | translate"
                        [amount]="financialOverviewSignal()?.availableBudget"
                        [ammountShouldBeGreen]="true"
                        [currency]="true"
                    />
                } @else {
                    <div class="p-6 rounded-md w-full h-[156px] items-center align-middle bg-[var(--base-2)]"></div>
                    <div class="p-6 rounded-md w-full h-[156px] items-center align-middle bg-[var(--base-2)]"></div>
                    <div class="p-6 rounded-md w-full h-[156px] items-center align-middle bg-[var(--base-2)]"></div>
                    <div class="p-6 rounded-md w-full h-[156px] items-center align-middle bg-[var(--base-2)]"></div>
                }
            </div>

            <hr class="border-separatorColor mb-8" />

            <!-- Applications Table -->
            <div class="flex justify-between mt-4">
                <h2 class="text-xl font-bold mb-4 mt-4">
                    {{ 'seasons.season-details.tabs.application' | translate }}
                </h2>
                @if (season(); as season) {
                    <div class="flex items-center gap-2">
                        @if (financialOverviewSignal()?.selectedApplications > 0) {
                            <sh-button
                                color="secondary"
                                size="max-content"
                                (click)="resetAutoSelectedApplications()"
                                ariaLabel="reset auto selected applications"
                                [label]="'seasons.season-details.buttons.deselect-applications' | translate"
                            />
                        }
                        <sh-button
                            color="secondary"
                            size="max-content"
                            (click)="autoSelectApplications()"
                            [disabled]="isAutoSelectDisabledSignal()"
                            ariaLabel="auto select applications"
                            [label]="'seasons.season-details.buttons.auto-select-application' | translate"
                        />
                    </div>
                }
            </div>
            <app-season-applications-table />
        </div>
    } @else {
        <div>
            <app-timeframe-card
                [title]="'seasons.season-details.setting-cards.timeframe-card.title' | translate"
                [startDate]="season()?.startDate"
                [endDate]="season()?.endDate"
                [applicationDeadlineDate]="season()?.applicationDeadline"
            />
            <div class="flex flex-row space-x-4">
                <div class="flex-1">
                    <app-budget-card
                        [title]="'seasons.season-details.setting-cards.budget-card.title' | translate"
                        [subtitle]="'seasons.season-details.setting-cards.budget-card.subtitle' | translate"
                        [totalBudget]="season()?.totalBudget"
                        [payoutElements]="season()?.payoutElements"
                    />
                </div>
                <div class="flex-1">
                    <app-social-criteria-card
                        [title]="'seasons.season-details.setting-cards.social-criteria-card.title' | translate"
                        [subtitle]="'seasons.season-details.setting-cards.social-criteria-card.subtitle' | translate"
                        [socialCriterias]="season()?.eligibilityCriteria"
                    />
                </div>
                <div class="flex-1">
                    <app-salary-card
                        [title]="'seasons.season-details.setting-cards.salary-card.title' | translate"
                        [subtitle]="'seasons.season-details.setting-cards.salary-card.subtitle' | translate"
                        [salaryRangeItems]="season()?.salaryRangeItems"
                    />
                </div>
            </div>
        </div>
    }
</div>
